import { useEffect, useState, type FormEvent } from "react";
import { supabase } from "../../lib/supabase.js";
import { useAuth } from "../../lib/auth.js";
import { Button, Select } from "../../lib/ui.js";
import { SectionCard, errorStyle, mutedStyle } from "./common.js";

interface SmsSettings {
  available: boolean;
  send_confirmations: boolean;
  send_reminders: boolean;
  reminder_hours: number;
}

const LEAD_CHOICES = [
  { hours: 2, label: "2 hours before" },
  { hours: 4, label: "4 hours before" },
  { hours: 24, label: "1 day before" },
  { hours: 48, label: "2 days before" },
];

/**
 * Booking texts are optional, twice over: the platform admin offers them to a business (Admin > SMS), and then the owner chooses here
 * which texts go out. Both start off. A business that has not been offered texts just sees a note.
 */
export function SmsSection() {
  const { activeBusinessId, memberships, isPlatformAdmin } = useAuth();
  const isOwner = isPlatformAdmin || memberships.some((m) => m.business_id === activeBusinessId && m.role === "owner");
  const [saved, setSaved] = useState<SmsSettings | null>(null);
  const [form, setForm] = useState<SmsSettings | null>(null);
  const [busy, setBusy] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!activeBusinessId) return;
    void supabase.rpc("rpc_portal_get_sms_settings", { p_business_id: activeBusinessId }).then(({ data }) => {
      const row = (Array.isArray(data) ? data[0] : data) as SmsSettings | null;
      if (row) {
        setSaved(row);
        setForm(row);
      }
    });
  }, [activeBusinessId]);

  if (!form || !saved) return null;

  if (!form.available) {
    return (
      <SectionCard title="Text messages" description="Send your customers a text when a booking is made, and a reminder before it.">
        <p style={mutedStyle}>Text messages are not switched on for your business. Contact us if you would like them.</p>
      </SectionCard>
    );
  }

  const changed =
    form.send_confirmations !== saved.send_confirmations || form.send_reminders !== saved.send_reminders || form.reminder_hours !== saved.reminder_hours;

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    if (!activeBusinessId || !form) return;
    setError(null);
    setJustSaved(false);
    setBusy(true);
    const { error: err } = await supabase.rpc("rpc_portal_set_sms_settings", {
      p_business_id: activeBusinessId,
      p_confirmations: form.send_confirmations,
      p_reminders: form.send_reminders,
      p_hours: form.reminder_hours,
    });
    setBusy(false);
    if (err) {
      setError(err.message);
      return;
    }
    setSaved(form);
    setJustSaved(true);
  }

  const disabled = !isOwner;

  return (
    <SectionCard
      title="Text messages"
      description="Texts go to the mobile number of the person who made the booking, in your business's time. Customers with only a landline are not texted. Bookings made before you turn confirmation texts on are not confirmed after the fact (and changing or cancelling one sends nothing, since the customer was never texted about it), but they still get reminders."
    >
      <form onSubmit={handleSave} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <label style={{ display: "flex", gap: 10, alignItems: "flex-start", cursor: disabled ? "default" : "pointer" }}>
          <input
            type="checkbox"
            checked={form.send_confirmations}
            disabled={disabled}
            onChange={(e) => {
              setJustSaved(false);
              setForm({ ...form, send_confirmations: e.target.checked });
            }}
            style={{ marginTop: 3 }}
          />
          <span style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <span style={{ fontSize: 13.5, fontWeight: 600, color: "var(--rc-text)" }}>Confirmation texts</span>
            <span style={{ fontSize: 12, color: "var(--rc-text-muted)" }}>
              Sent as soon as a booking is made, by phone or on the web. If the booking is moved to a new time the customer is sent the new time, and
              if it is cancelled they are told it is cancelled.
            </span>
          </span>
        </label>

        <label style={{ display: "flex", gap: 10, alignItems: "flex-start", cursor: disabled ? "default" : "pointer" }}>
          <input
            type="checkbox"
            checked={form.send_reminders}
            disabled={disabled}
            onChange={(e) => {
              setJustSaved(false);
              setForm({ ...form, send_reminders: e.target.checked });
            }}
            style={{ marginTop: 3 }}
          />
          <span style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <span style={{ fontSize: 13.5, fontWeight: 600, color: "var(--rc-text)" }}>Reminder texts</span>
            <span style={{ fontSize: 12, color: "var(--rc-text-muted)" }}>
              Never sent between 8 pm and 8 am: a reminder that would fall then goes out earlier, at 8 pm. Not sent for a booking made just before the
              reminder was due.
            </span>
          </span>
        </label>

        {form.send_reminders && (
          <label style={{ display: "flex", flexDirection: "column", gap: 5, fontSize: 13, fontWeight: 600, color: "var(--rc-text-muted)", maxWidth: 260 }}>
            Send the reminder
            <Select
              value={form.reminder_hours}
              disabled={disabled}
              onChange={(e) => {
                setJustSaved(false);
                setForm({ ...form, reminder_hours: Number(e.target.value) });
              }}
            >
              {LEAD_CHOICES.map((c) => (
                <option key={c.hours} value={c.hours}>
                  {c.label}
                </option>
              ))}
            </Select>
          </label>
        )}

        {disabled ? (
          <p style={mutedStyle}>Only an owner of the business can change these.</p>
        ) : (
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <Button type="submit" variant="primary" disabled={busy || !changed}>
              {busy ? "Saving..." : "Save text message settings"}
            </Button>
            {justSaved && <span style={{ fontSize: 13, color: "var(--rc-success)" }}>Saved.</span>}
          </div>
        )}
        {error && <p style={errorStyle}>{error === "sms_not_available" ? "Text messages are not switched on for your business." : error}</p>}
      </form>
    </SectionCard>
  );
}
